<template>
  <div
    :id="chartId"
    class="chart-stacked"
  >
    <div
      ref="chartArea"
      :class="`svg-wrapper svg-wrapper-${chartId}`"
      :style="extraPadding"
    >
      <svg></svg>
    </div>
    <div
      :ref="`chart-popover-${chartId}`"
      class="popover-wrapper outline-none"
      tabindex="0"
      @focusout="popoverFocusOut"
    >
      <AppChartPopover
        v-show="hasPopoverData"
        :id="`chart-popover-${chartId}`"
        :position="popoverPosition"
        :trigger="popoverTrigger"
        :column-data="currentColumnData"
        @close="triggerHidePopover"
      >
        <template #popover-header>
          <slot
            name="popover-header"
            :column-data="currentColumnData"
          />
        </template>

        <template #popover-content>
          <slot
            name="popover-content"
            :column-data="currentColumnData"
          />
        </template>
      </AppChartPopover>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';

import { ChartStacked } from '~/mixins';

export default {
  name: 'AppChartStackedHorizontal',
  mixins: [ChartStacked],
  data: () => ({
    isVertical: false,
  }),
  computed: {
    chartHeight() {
      // If columnWidth is set, chart has no height limit and svg height must be calculated based on total number of bars
      if (this.fixedWidthBars) {
        const barTotal = this.formattedChartData.length * this.fixedWidthBars;

        return this.columnSpacing ? barTotal + barTotal * this.columnSpacing : barTotal;
      }

      if (this.fixedHeight) {
        return this.fixedHeight;
      }

      return this.dynamicHeight ? this.$refs.chartArea.clientHeight : this.height;
    },
  },
  methods: {
    buildChart() {
      this.chart = d3.select(this.$el);
      this.svg = d3.select(this.$el).select('svg');
      this.inner = d3.select(this.$el).select('svg').append('g');
      this.maxValue = d3.max(this.formattedChartData, (column) => column.total);

      this.cleanupTooltips();
      this.setChartColors();
      this.setChartSizing();

      this.x = d3.scaleLinear().range([this.height, 0]).domain([0, this.maxValue]);
      this.y = d3
        .scaleBand()
        .domain(this.formattedChartData.map((column, index) => `${column.label}__${index}`))
        .range([0, this.height])
        .paddingInner(this.columnSpacing);

      this.generateXAxis();
      this.generateYAxis();
      this.createBars();

      this.loading = false;
    },
    createBarSection(data, radius, isEmpty = false) {
      let _vEnd = this.x(data.vEnd);
      const _vBegin = this.x(data.vBegin);

      // If the bar is empty, we draw 1px line
      if (isEmpty) {
        _vEnd = _vBegin - 1;
      }

      return `M${_vEnd - radius},0
        a${radius},${radius} 0 0 1 ${radius},${radius}
        v${this.y.bandwidth() - 2 * radius}
        a${radius},${radius} 0 0 1 ${-radius},${radius}
        h${_vBegin - _vEnd + radius}
        v${-this.y.bandwidth()}Z`;
    },
    generateXAxis() {
      if (this.hasXAxis) {
        const maxValue = d3.max(this.formattedChartData, (column) => column.total);
        const xAxis = d3.axisBottom(
          d3
            .scaleLinear()
            .domain([Math.ceil(maxValue / 10) * 10, 0])
            .range([0, this.width]),
        );

        this.svg
          .append('g')
          .attr('transform', `translate(-2,${this.height})`)
          .attr('class', 'xAxis')
          .call(
            xAxis
              .ticks(Math.min(Math.ceil(maxValue / 10), 10))
              .tickSize(-this.height) // let the axes be 100% width of the chart
              .tickFormat((d) => `${d}${this.yTickFormat}`),
          );
      }
    },
    generateYAxis() {
      if (this.hasYAxis) {
        const yAxis = d3.axisLeft(this.y);

        this.inner
          .append('g')
          .call(yAxis)
          .attr('class', `yAxis yAxis-${this.chartId}`)
          .attr('style', `transform:translateX(-0.5px)`)
          .call((g) =>
            // Removing the perpendicular lines at the ends of the axis
            g.select('.domain').attr(
              'd',
              d3.line()([
                [0, 0],
                [0, this.height],
              ]),
            ),
          )
          .selectAll('.tick text')
          .attr('style', `transform:translateX(-8px)`)
          .call(this.labelCustomization, this.yAxisWidth - 8);
      }
    },
    updateAxis() {
      this.svg.selectAll('.xAxis').call(d3.axisBottom().scale(this.x));
    },
    resizeChart() {
      const yAxis = document.querySelector(`.yAxis-${this.chartId}`);
      const yAxisBounds = yAxis.getBoundingClientRect();
      const elBounds = this.$el.getBoundingClientRect();

      // When resizing, we need to get the new width of the containing div and update the chart accordingly
      this.height = this.chartHeight;
      this.width = elBounds.width - (this.hasYAxis ? yAxisBounds.width * 1.5 : 0);
      this.svg.attr('class', 'chart-svg').attr('width', this.width + this.margin.left + this.margin.right);

      this.x = d3.scaleLinear().domain([0, this.maxValue]).nice().range([0, this.width]);

      this.drawBars();
      this.updateAxis();
    },
  },
};
</script>
